import { useGPFormContext } from '@/contexts/GPFormContext';
import { WithRequired } from '@/types/util';
import { makeStyles } from 'tss-react/mui';
import { GoogleMap, InfoWindow, Marker } from '@react-google-maps/api';
import { Dispatch, SetStateAction, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PlaceInterestType, usePlaceOfInterestCard } from '../GPCards/PlaceOfInterestCard';
import { GlobalStyles } from '@mui/material';

const AUSTIN_CENTER = { lat: 30.2672, lng: -97.7431 };

const useStyles = makeStyles()(() => ({
  root: {
    position: 'sticky',
    top: 20,
    bottom: 20,
  },

  topContainer: {
    boxSizing: 'border-box',
    border: '1px solid transparent',
    width: 240,
    borderRadius: 3,
    boxShadow: '0 2px 6px rgba(0, 0, 0, 0.3)',
    fontSize: 14,
    outline: 'none',
    textOverflow: 'ellipses',
    position: 'absolute',
    left: '50%',
    marginLeft: '-120px',
    marginTop: 10,
    backgroundColor: 'white',
    padding: 0,
  },

  mapButton: {
    width: '100%',
    padding: 5,
    cursor: 'pointer',
    border: 'none',
    margin: 0,
  },
}));

let containerStyle = {
  width: '100%',
  height: 500,
};

type GoogleMapWrappedProps = {
  data?: MapViewItem[];
  listViewData?: ListViewItem[];
  disableResearchButton?: boolean;
  center?: { lat: number; lng: number };
};

export default function GoogleMapWrapped({
  data = [],
  listViewData = [],
  disableResearchButton,
  center,
}: GoogleMapWrappedProps) {
  const { classes } = useStyles();
  const { t } = useTranslation();

  const [mapRef, setMapRef] = useState<google.maps.Map | null>(null);
  const [selectedPlaceLatLng, setSelectedPlaceLatLng] = useState<string | null>(null);
  const [initInfoWindow, setInitInfoWindow] = useState(false);
  const [infoOpen, setInfoOpen] = useState(false);
  const [zoom, setZoom] = useState(10);
  //                                👆 zoom=10 is a heuristic initial zoom
  //                    google map component requires an initial value for its 1st render

  const [allowBoundsChangedListener, setAllowBoundsChangedListener] = useState(false);

  const { setValue, handleSubmit, onSubmit } = useGPFormContext();

  // resize listener - dynamically update container height
  useEffect(() => {
    const setMapHeight = () => {
      containerStyle = {
        ...containerStyle,
        height: window.innerHeight - 40,
      };
    };
    setMapHeight();
    window.addEventListener('resize', setMapHeight);
    return () => window.removeEventListener('resize', setMapHeight);
  }, []);

  const dataGrpByLatLng = useMemo(
    () =>
      data.reduce<Record<string, ValidMapViewItem[] | undefined>>((acc, item) => {
        if (!item.latitude || !item.longitude) {
          return { ...acc };
        }
        const placeLatLng = JSON.stringify([item.latitude, item.longitude]);
        return {
          ...acc,
          [placeLatLng]: [...(acc[placeLatLng] ?? []), { ...item }] as ValidMapViewItem[],
        };
      }, {}),
    [data],
  );

  const latLngBounds = useMemo(() => {
    // filter data items that do have lat,lng
    const validDataItems = data?.filter(place => place.latitude && place.longitude);
    if (!validDataItems?.length) return;

    const bounds = new window.google.maps.LatLngBounds();
    for (const place of validDataItems) {
      bounds.extend({
        lat: Number(place.latitude),
        lng: Number(place.longitude),
      });
    }
    return bounds;
  }, [data]);

  const boundsChangedHandler = () => {
    // refer https://stackoverflow.com/a/22712105

    // for at most once effective execution
    if (!allowBoundsChangedListener) return;
    setAllowBoundsChangedListener(false);

    const newBoundsZoom = mapRef?.getZoom();
    if (!newBoundsZoom) return;

    setZoom(newBoundsZoom - 2);
    // another heuristic,  👆 offset zoom level to ensure no marker is on the edge.

    // set a minimum zoom
    // if you got only 1 marker or all markers are on the same address map will be zoomed too much.
    if (newBoundsZoom > 13) {
      setZoom(13);
    }
  };

  // on google map load event - capture ref and dynamically set bounds
  const loadHandler = (map: google.maps.Map) => {
    // Store a reference to the google map instance in state
    setMapRef(map);

    // since map's now loaded, allow bounds change listener
    setAllowBoundsChangedListener(true);

    // fit map's bounds, but 1st ensure bounds_changed listener is added (refer https://stackoverflow.com/a/22712105)
    map.addListener('bounds_changed', boundsChangedHandler);
    latLngBounds && map.fitBounds(latLngBounds);
  };

  // refresh map view search results with updated center coordinates
  const redoMap = () => {
    const newCenter = mapRef?.getCenter();
    setValue('coordinates', newCenter);
    setValue('location', null);
    handleSubmit(onSubmit)();
  };

  const dataMarkers = useMemo(() => {
    const markerClickHandler = (placeLatLng: string) => {
      // Remember which place was clicked
      setSelectedPlaceLatLng(placeLatLng);

      // first close currently open info, if any
      // Required so clicking a 2nd marker works as expected
      setInfoOpen(false);

      setInfoOpen(true);

      // If you want to zoom in a little on marker click
      setZoom(zoom => Math.max(zoom, 13));

      // if you want to center the selected Marker
      // setCenter(place.pos);
    };
    const placesOfInterest = Object.keys(dataGrpByLatLng);
    return (
      <>
        {placesOfInterest.map(place => (
          <MarkerWrapped key={place} placeLatLng={place} markerClickHandler={markerClickHandler} />
        ))}
      </>
    );
  }, [dataGrpByLatLng]);

  const infoWindowOpenIntent = infoOpen && selectedPlaceLatLng;

  // defer initializing info window until a marker is cliked
  useEffect(() => {
    if (infoWindowOpenIntent && !initInfoWindow) setInitInfoWindow(true);
  }, [infoWindowOpenIntent, initInfoWindow]);

  return (
    <div className={classes.root}>
      {/* <LoadScript */}
      {/*  googleMapsApiKey="AIzaSyBu_bPaQiEXSUyYCoE7qrQkRFSPrN69WRw" */}
      {/* > */}
      <GoogleMap
        id="google-map"
        mapContainerStyle={containerStyle}
        center={center ?? latLngBounds?.getCenter() ?? AUSTIN_CENTER}
        zoom={zoom}
        onLoad={loadHandler}
        onClick={() => setInfoOpen(false)}
        onBoundsChanged={boundsChangedHandler} // refer https://stackoverflow.com/a/22712105
      >
        {dataMarkers}
        {initInfoWindow && (
          <InfoWindowWrapped
            selectedPlaceLatLng={selectedPlaceLatLng}
            dataGrpByLatLng={dataGrpByLatLng}
            listViewData={listViewData}
            infoOpen={infoOpen}
            setInfoOpen={setInfoOpen}
          />
        )}
        {!disableResearchButton && (
          <div className={classes.topContainer}>
            <button type="button" className={classes.mapButton} onClick={redoMap}>
              {t('Redo Search in Map')}
            </button>
          </div>
        )}
      </GoogleMap>
      <GlobalStyles styles={googleMapStyleOverrides} />
      {/* </LoadScript> */}
    </div>
  );
}

type MapViewItem = {
  unique_id: number | string;
  latitude?: string;
  longitude?: string;
};

type ValidMapViewItem = WithRequired<MapViewItem, 'latitude' | 'longitude'>;

type MarkerWrappedProps = {
  placeLatLng: string;
  markerClickHandler: (placeLatLng: string) => void;
};

function MarkerWrapped({ placeLatLng, markerClickHandler }: MarkerWrappedProps) {
  const [selected, setSelected] = useState(false);
  const [lat, lng] = JSON.parse(placeLatLng) as [string, string];
  return (
    <Marker
      position={{ lat: parseFloat(lat), lng: parseFloat(lng) }}
      icon={selected ? '/v1/images/mapmarker_hover.png' : '/v1/images/mapmarker.png'}
      onMouseOver={() => setSelected(true)}
      onMouseOut={() => setSelected(false)}
      clickable
      onClick={() => markerClickHandler(placeLatLng)}
    />
  );
}

type ListViewItem = {
  unique_id?: number | string;
};

type InfoWindowWrappedProps = {
  selectedPlaceLatLng?: string | null;
  dataGrpByLatLng?: Record<string, ValidMapViewItem[] | undefined>;
  listViewData?: ListViewItem[];
  infoOpen: boolean;
  setInfoOpen: Dispatch<SetStateAction<boolean>>;
};

function InfoWindowWrapped(props: InfoWindowWrappedProps) {
  const {
    selectedPlaceLatLng,
    dataGrpByLatLng = {},
    listViewData = [],
    infoOpen,
    setInfoOpen,
  } = props;

  // index list view data by unique_id
  const listViewDataDict = useMemo(
    () =>
      listViewData.reduce<Record<number | string, ListViewItem | undefined>>(
        (acc, listViewItem) => {
          if (!listViewItem.unique_id) return { ...acc };
          return { ...acc, [listViewItem.unique_id]: listViewItem };
        },
        {},
      ),
    [listViewData],
  );

  // wrangle map-view and list-view data
  // join map-view data and list-view data based on unique_id
  // group by [lat, lng]
  const mergedDataGrpByLatLng = useMemo(() => {
    const mergedDataGrpByLatLng: Record<string, ValidMapViewItem[] | undefined> = {};
    for (const [placeLatLng, interests] of Object.entries(dataGrpByLatLng)) {
      mergedDataGrpByLatLng[placeLatLng] = interests?.map(item => ({
        ...item,
        ...(listViewDataDict[item.unique_id] ?? {}),
      }));
    }
    return mergedDataGrpByLatLng;
  }, [dataGrpByLatLng, listViewDataDict]);

  const PlaceOfInterestCard = usePlaceOfInterestCard();

  return !infoOpen || !selectedPlaceLatLng
    ? null
    : (() => {
        const [lat, lng] = JSON.parse(selectedPlaceLatLng) as [string, string];
        const interests = mergedDataGrpByLatLng[selectedPlaceLatLng];
        return (
          <InfoWindow
            options={{ maxWidth: 300 }}
            position={{
              lat: parseFloat(lat),
              lng: parseFloat(lng),
            }}
            onCloseClick={() => setInfoOpen(false)}
          >
            <PlaceOfInterestCard interests={interests as PlaceInterestType[]} />
          </InfoWindow>
        );
      })();
}

const googleMapStyleOverrides = {
  'button.gm-ui-hover-effect > span': { display: 'none !important' },
  'button.gm-ui-hover-effect': {
    marginRight: '3px !important',
    marginTop: '3px !important',
    opacity: '1 !important',
    backgroundImage:
      "url('data:image/svg+xml,%3Csvg%20width%3D%2224%22%20height%3D%2224%22%20viewBox%3D%220%200%2024%2024%22%20fill%3D%22none%22%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%3E%0A%3Cg%20clip-path%3D%22url(%23clip0_3461_25)%22%3E%0A%3Cpath%20d%3D%22M22.9075%201.09229L1.09229%2022.9075M1.09229%201.09229L22.9075%2022.9075%22%20stroke-width%3D%222.1848%22%20stroke-linecap%3D%22round%22%20stroke-linejoin%3D%22round%22%20stroke%3D%22black%22%2F%3E%0A%3C%2Fg%3E%0A%3Cdefs%3E%0A%3CclipPath%20id%3D%22clip0_3461_25%22%3E%0A%3Crect%20width%3D%2224%22%20height%3D%2224%22%20fill%3D%22white%22%2F%3E%0A%3C%2FclipPath%3E%0A%3C%2Fdefs%3E%0A%3C%2Fsvg%3E') !important",
    backgroundRepeat: 'no-repeat !important',
    backgroundSize: '12px 12px !important',
    backgroundPosition: 'center center !important',
    padding: '6px !important',
    borderRadius: '50% !important',
    backgroundColor: 'white !important',
    top: 'unset !important',
    right: 'unset !important',
    zIndex: 1,
  },
  'button.gm-ui-hover-effect:hover': {
    backgroundColor: 'rgba(244, 244, 244, 0.6) !important',
  },
  '.gm-style .gm-style-iw-c': {
    display: 'flex !important',
    padding: '0 !important',
    borderRadius: '12px !important',
    justifyContent: 'flex-end !important',
  },
  '.gm-style-iw-d': { overflow: 'auto !important' },
};
